import React from "react";
import { Polygon } from "@react-google-maps/api";
import { compose } from "recompose";
import { connect } from "react-redux";
import { no_op } from 'utilities';
import { shapeFields } from './shapeFields';
import {
  createCustomShapeCoordinates,
  createPolygonCoordinates,
  getColorForShape,
} from './helpers';
import {
  setSelectedShapes,
  setSelectedCustomShape,
  setSelectedShapeId,
} from 'store/actions';

function Shape({
  shape,
  opacityOverride,
  shapesData,
  shapesColoringType,
  shapesColoringDataType,
  onClick = no_op,
  isSelected = false,
  clickable = true,
  shapeColorPalette,
  isCustomShape,
  isSelectedShape,
  ...props
}) {
  // == Constants ================================================

  const coords = isCustomShape
    ? createCustomShapeCoordinates(shape)
    : createPolygonCoordinates(shape);

  // == JSX ====================================================

  if (coords) {
    const colorMatrix = getColorForShape(
      shapeColorPalette,
      shape,
      shapesData,
      shapesColoringType,
      shapesColoringDataType
    );

    const options = isCustomShape
      ? {
          clickable,
          fillColor: '#000000',
          fillOpacity: isSelected ? 0.7 : 0.35,
          strokeColor: '#000000',
          strokeOpacity: isSelected ? 0.8 : 0.4,
          strokeWeight: 1,
          zIndex: 1,
        }
      : isSelectedShape
      ? {
          clickable,
          fillColor: colorMatrix.maxValueColor,
          fillOpacity: 0.9,
          strokeColor: colorMatrix.maxValueColor,
          strokeOpacity: 1,
          strokeWeight: 4,
          zIndex: 1000,
        }
      : {
          clickable,
          fillColor: isSelected
            ? colorMatrix.maxValueColor
            : colorMatrix.valueColor,
          fillOpacity: isSelected ? 0.7 : 0.35,
          strokeColor: isSelected
            ? colorMatrix.maxValueColor
            : colorMatrix.valueColor,
          strokeOpacity: isSelected ? 0.8 : 0.4,
          strokeWeight: isSelected ? 3 : 1,
          zIndex: 999,
        };

    return (
      <Polygon paths={coords} options={options} onClick={onClick} {...props} />
    );
  } else {
    return null;
  }
}

const AssumptionsShapeManager = ({
  shapes,
  shapesData,
  shapesGeoType,
  customShapes,
  setSelectedShapes,
  selectedShapes,
  shapesColoringType,
  selectedShapeId,
  creatingNewShape,
  selectedCustomShape,
  showCustomShapes,
  setSelectedCustomShape,
  shapeColorPalette,
  setSelectedShapeId,
}) => {
  // == Shapes =================================================

  let mapShapes = null;
  let selectedMapShapes = null;
  let customMapShapes = null;

  // == Event Handlers =========================================

  const handleAddShapeClicked = (shape) => {
    if (creatingNewShape && shapesGeoType === 'auto') {
      alert(
        "When creating a new shape, please select an option other than 'auto'."
      );
      return;
    }

    setSelectedShapeId(shape.id);

    if (creatingNewShape) {
      const newShape = {
        id: shape.id,
        level: shapesGeoType,
        shape: shape,
      };

      var existingShape = selectedShapes.find((oo) => oo.id === newShape.id);

      if (!existingShape) {
        const newSelectedShapes = [...selectedShapes, newShape];
        setSelectedShapes(newSelectedShapes);
        return;
      }

      const indexOf = selectedShapes.indexOf(existingShape);
      const newSelectedShapes = [...selectedShapes];
      newSelectedShapes.splice(indexOf, 1);
      setSelectedShapes(newSelectedShapes);
    }
  };

  const handleCustomShapeClick = (shape) => {
    const { shapeId } = shape;

    if (selectedCustomShape && selectedCustomShape.shapeId === shapeId) {
      setSelectedCustomShape(null);
      return;
    }

    setSelectedCustomShape(shapeId);
  };

  // == JSX ==========================================================

  const dataType = shapeFields.find(
    (field) => field.dataField === shapesColoringType
  )?.dataType;

  if (shapesGeoType && shapes && shapes.length > 0) {
    mapShapes = shapes.map((shape) => {
      const isSelected = selectedShapes.map((oo) => oo.id).includes(shape.id);
      if (!isSelected) {
        return (
          <Shape
            shape={shape}
            key={shape.id}
            onClick={() => handleAddShapeClicked(shape)}
            shapesData={shapesData}
            shapesColoringType={shapesColoringType}
            shapesColoringDataType={dataType}
            shapeColorPalette={shapeColorPalette}
            isCustomShape={false}
            isSelectedShape={selectedShapeId === shape.id}
          />
        );
      }
    });
  }

  if (selectedShapes && selectedShapes.length > 0) {
    selectedMapShapes = selectedShapes.map((selectedShape) => {
      return (
        <Shape
          shape={selectedShape.shape}
          isSelected={true}
          key={selectedShape.id}
          onClick={() => handleAddShapeClicked(selectedShape.shape)}
          shapesData={shapesData}
          shapesColoringType={shapesColoringType}
          shapesColoringDataType={dataType}
          shapeColorPalette={shapeColorPalette}
          isCustomShape={false}
          isSelectedShape={false}
        />
      );
    });
  }

  if (showCustomShapes && customShapes) {
    customMapShapes = customShapes.map((shape) => {
      const isSelected = selectedCustomShape?.shapeId === shape.shapeId;
      return (
        <Shape
          isCustomShape={true}
          shape={shape}
          key={shape.shapeId}
          isSelected={isSelected}
          shapesData={shapesData}
          onClick={() => handleCustomShapeClick(shape)}
          shapesColoringType={shapesColoringType}
          shapesColoringDataType={dataType}
        />
      );
    });
  }

  return (
    <>
      {mapShapes}
      {selectedMapShapes}
      {customMapShapes}
    </>
  );
};

// == Connect =====================================================

const mapState = ({ assumptionMapState, assumptionGroupsState }) => {
  const { colorPalettes, colorPaletteId } = assumptionMapState;
  return {
    shapes: assumptionMapState.shapes,
    shapesGeoType: assumptionMapState.shapesGeoType,
    shapesData: assumptionMapState.shapesData,
    shapesColoringType: assumptionMapState.shapesColoringType,
    selectedShapeId: assumptionMapState.selectedShapeId,
    creatingNewShape: assumptionGroupsState.creatingNewShape,
    customShapes: assumptionGroupsState.customShapes,
    selectedShapes: assumptionGroupsState.selectedShapes,
    selectedCustomShape: assumptionGroupsState.selectedCustomShape,
    showCustomShapes: assumptionGroupsState.showCustomShapes,
    shapeColorPalette: colorPalettes.find(
      (palette) => palette.id === colorPaletteId
    ),
  };
};

const mapDispatchToProps = {
  setSelectedShapes,
  setSelectedCustomShape,
  setSelectedShapeId,
};

export default compose(
  connect(
    mapState,
    mapDispatchToProps
  )
)(AssumptionsShapeManager);
